/**
 * @file        dcp-xhr.js              XMLHttpRequest library for DCP, implemented via
 *                                      the xmlhttprequest-ssl NPM package, that transparently
 *                                      enables KeepAlive (for http and https) in a way that
 *                                      that is call-compatible with the browser code.
 *
 *                                      If this module is invoked in an environment where there
 *                                      is a self.XMLHttpRequest already defined (eg browser),
 *                                      it is assumed that that implementation behaves correctly
 *                                      and this module's export is replaced by that function.
 *
 * @module      dcp-xhr
 * @author      Wes Garland, wes@kingsds.network
 * @date        June 2019
 */
'use strict';

const { requireNative, process } = require('dcp/dcp-client/webpack-native-bridge')
const debugging = require('../debugging').scope('xhr');
const debug = process.env.DCP_XHR_DEBUG; /** @todo: deprecate DCP_XHR_DEBUG in favour of DCP_DEBUG=xhr /wg Feb 2025 */
const dcpConfig = globalThis.dcpConfig;

var vlog  
if (debug?.match(/\bverbose\b/) || debugging('verbose')) {
  /** Verbose logging function: first argument should be an id number */
  vlog = function vlog() {
    var argv = Array.from(arguments)
    argv.unshift('DCP-XHR')
    if (typeof argv[1] === 'number')
      argv[1] += '>'
    else
      argv[0] += '>'
    console.debug.apply(null, argv);
  }
}
else
  vlog = Function()

/** Return a node Agent (or workalike) object that matches the given URL.
 *  Agents are cached per-protocol so that xmlhttprequest-ssl can actually
 *  implement HTTP KeepAlive
 */
function getAgent(url) {
  let prot, agent
  let urlo = new URL(url);
  let pxHost = process.env.DCP_XHR_PROXY || dcpConfig?.network?.proxy;

  if (!getAgent.cache)
    getAgent.cache = {} /* one agent per protocol */

  prot = urlo.protocol.replace(/:/,'')
  if (prot !== 'http' && prot !== 'https')
    throw new Error('Protocol must be http: or https:!')

  if (getAgent.cache[prot])
    return getAgent.cache[prot]

  if (pxHost) {
    /* px support experimental! /wg jul 2019 */
    let pxAgent = requireNative(prot + '-proxy-agent')
    if (debug || debugging())
      console.debug('* XHR using proxy', pxHost);
    agent = new pxAgent(pxHost)
  } else {
    agent = new (requireNative(prot).Agent)({
      keepAlive: true,
      keepAliveMsecs: dcpConfig?.network?.httpKeepAliveMsecs || 180000
    });
  }

  getAgent.cache[prot] = agent
  return agent
}

/** 
 *  XMLHttpRequest constructor. Instanciates a Proxy which memoizes function calls and
 *  property writes before the open() method is invoked. Property accesses before open() 
 *  return values from a dummy instance.  
 *
 *  When open() is invoked, we examine the protocol in the URL and set up an instance of
 *  require('xmlhttprequest-ssl').XMLHttpRequest. We look at the pending call/writes memo,
 *  applying pending method calls and property writes to this object, and then we invoke
 *  its own open() method.
 *
 *  After open() is invoked, we proxy all property accesses to the 'real' xhr.
 *
 *  The reason for these gymnastics is a design error in the xmlhttprequest-ssl module; it
 *  requires knowledge of the Agent type, which depends on the protocol, in the constructor,
 *  which is contrary to the spec.; the protocol is only officially known once the URL is
 *  known.
 *
 *  @constructor
 *  @see xmlhttprequest-ssl
 *  @see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest
 *  @returns   {object}     instance of Proxy
 */
exports.XMLHttpRequest = function dcpXhr$$XMLHttpRequest() {
  var thisId = exports.XMLHttpRequest.id = (exports.XMLHttpRequest.id || 0) + 1

  vlog(thisId, `Constructor`, (new Error('VLOG XHR').stack || '').split('\n')[2])
  const XMLHttpRequest = requireNative('xmlhttprequest-ssl').XMLHttpRequest;

  let dummy = new XMLHttpRequest() /* dummy request, used for type-testing */
  let pending = []                 /* list of pending function calls or property assignments, memoized before open() */
  let xhr                          /* actual XMLHttpRequest object; instanciated during open()  */

  function debugLabel(obj, prop) {
    if (typeof obj[prop] === 'function')
      return '[pxFunction' + (obj === xhr ? " " : " dummy ") + (obj[prop].name || prop) + ']'
    return obj[prop]
  }

  /* Apply the pending call/writes memo, then set up so that proxy will operate directly on xhr */
  let open = (function dcpXhr$$XMLHttpRequest$open(requestType, url) {
    var referer = exports.getReferer();

    vlog(thisId, `open`, arguments)
    xhr = new XMLHttpRequest({agent: getAgent(url)})
    pending.forEach((capture) => {
      if (capture.type === 'invoke') {
        xhr[capture.prop].apply(xhr, capture.arguments)
      } else {
        xhr[capture.prop] = capture.value
      }
    })

    dummy = pending = undefined
    xhr.open.apply(xhr, arguments)
    xhr.setDisableHeaderCheck(true)
    if (debug || debugging())
      console.debug(' * XHR:', url);
    if (referer)
      xhr.setRequestHeader('Referer', referer);
  }).bind(this)

  return new Proxy({} /**< target */, {
    /* before .open() - return properties of dummy object
     * after  .open() - return properties of real object
     */
    get: function dcpXhr$$XMLHttpRequest$pxGetter(target, prop) {
      if (xhr) {
        const p = typeof prop === 'string' ? prop : '{Symbol}';
        vlog(thisId, `get ${p}=${debugLabel(xhr, p)}`)
        if (typeof xhr[prop] === 'function')
          return (function pxFunctionWrapper() {
            vlog(thisId, `invoke`, prop, arguments)
            return xhr[prop].apply(xhr, arguments)
          }).bind(xhr)
        return xhr[prop]
      }
      vlog(thisId, `get dummy ${prop}=${debugLabel(dummy, prop)}`)

      if (prop === 'open')
        return open
      if (typeof dummy[prop] === 'function') {
        return function dcpXhr$$XMLHttpRequest$captureFunctionCall() {
          if (xhr)
            xhr[prop]  /* don't memoize if .open() happened since property retrieval */
          else
            pending.push({type: 'invoke', prop: prop, arguments: Array.from(arguments)})
        }
      }
      else
        return dummy[prop]
    },

    /* memoize assignments or pass through to xhr if we're past open() */
    set: function dcpXhr$$XMLHttpRequest$pxSetter(target, prop, value) {
      if (xhr) {
        let old = debugLabel(xhr, prop)
        xhr[prop] = value
        vlog(thisId, `set ${prop} ${old} -> ${value}`);
      }
      else {
        pending.push({type: 'assignment', prop: prop, value: value})
        vlog(thisId, `memoize ${prop}=${value}`)
      }
      return true
    },

    has: function dcpXhr$$XMLHttpRequest$pxHas(target, key) {
      return key in (xhr || dummy)
    },

    apply: function dcpXhr$$XMLHttpRequest$pxApply(target, thisArg, argumentsList) {
      return (xhr || dummy).apply(target, thisArg, argumentsList);
    },

    ownKeys: function dcpXhr$$XMLHttpRequest$pxOwnKeys(target) {
      return Object.getOwnPropertyNames(xhr || dummy).concat(Object.getOwnPropertySymbols(xhr || dummy))
    },

    getOwnPropertyDescriptor: function dcpXhr$$XMLHttpRequest$pxGetOwnPropertyDescriptor(target, prop) {
      return Object.getOwnPropertyDescriptor(xhr || dummy, prop);
    },
  })
}

var _referer;
exports.getReferer = function dcpXhr$$getReferer() {
  _referer = process.env.DCP_XHR_REFERER || (dcpConfig?.network && dcpConfig.network['http-referer']);
  
  if (!_referer) {
    if (module && module.parent && module.parent.filename) {
      _referer = requireNative('path').basename(module.parent.filename) + '/dcp-xhr'
    } else {
      /* webpack, probably */
      _referer = String((new Error().stack || '').split('\n')[1].match(/\(.*\)/)).replace(/^\(/,'').replace(/\)$/,'').replace(/\?.*/,'')
      _referer = _referer.substring(_referer.lastIndexOf(requireNative('path').sep) + 1);
      _referer = _referer.substring(0, _referer.indexOf(':'));
    }
  }
}

exports.setReferer = function dcpXhr$$setReferer(referer) {
  _referer = referer;
}
